/**
 * The alternate rate access codes the hotel loaded THIS agreement under.
 *
 * The data gap Stacie returned to unprompted in three sessions and called the
 * thing she keeps hammering on. `customer_rac` holds the code assigned to a
 * CUSTOMER for a GDS; this holds the one the hotel actually loaded a particular
 * agreement under, which is frequently different — a chain cannot authorise two
 * customers under the same leading characters, so a collision forces another
 * code. Her words on frequency: "almost every time."
 *
 * ── Why it lives in the drawer and not the edit modal ──────────────────────
 * It is learned DURING loading, not negotiated: the hotel replies "we could
 * only do X, not Y" and whoever is loading types it in. Putting it behind the
 * sourcing-only edit modal would make it unreachable at the moment it arrives.
 * The same reasoning keeps the rate table writable after acceptance.
 *
 * ── Grouped by GDS, because the GDS is what makes the code actionable ──────
 * The loading team types a code INTO a system. Measured on the real export:
 * 70% of the 847 recorded values are one bare code, and the rest include cells
 * naming two or three systems with a different code for each, which is why this
 * is a list rather than a field.
 */
import { useState } from 'react';
import { Modal } from '@/features/crm/components/Modal';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';

/** Mirrors the vocabulary `customer_rac` already stores, so a code reads the
 *  same on the customer panel and here. Sabre first and defaulted — it is 82%
 *  of the export's Current GDS column, and the rate-audit form settled the same
 *  way: "if [GDS] could default to Sabre, that would be great." */
const GDS_OPTIONS = ['Sabre', 'TP+', 'Worldspan', 'Amadeus'] as const;

/**
 * NOT `AgreementRac` — that name is already taken, by the rollup's list of codes
 * a rate was LOADED under (derived from `agreement_load`). This is the
 * alternate code the hotel loaded the agreement under, which is a different
 * fact from a different table, and two interfaces sharing a name in one
 * codebase is how the wrong one gets imported.
 */
export interface AlternateRac {
  id: string;
  rac: string;
  gds: string;
  note: string | null;
}

export function AlternateRacs({
  agreementId,
  canEdit,
  onChanged,
  racs,
  testId = 'agreement-detail-racs',
}: {
  agreementId: string;
  canEdit: boolean;
  onChanged: () => void;
  racs: AlternateRac[];
  testId?: string;
}) {
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function remove(rac: AlternateRac) {
    if (busy) return;
    if (
      !window.confirm(
        `Remove ${rac.rac} for ${rac.gds}? The agreement keeps a record of what was removed.`,
      )
    ) {
      return;
    }
    setBusy(rac.id);
    setError(null);
    try {
      await apiFetch(`/api/btp/agreement-racs/${rac.id}`, { method: 'DELETE' });
      onChanged();
    } catch (err) {
      setError(messageOf(err, 'The code could not be removed.'));
      setBusy(null);
    }
  }

  return (
    <>
      {error && (
        <div
          className="banner banner-error"
          data-testid={`${testId}-error`}
          role="alert"
        >
          {error}
        </div>
      )}

      {racs.length === 0 ? (
        <p className="empty-inline" data-testid={`${testId}-empty`}>
          {/* Stated rather than left blank: an empty list here means "the hotel
              loaded under the customer's own code", which is a real answer and
              the common one. A bare em-dash would read as "nobody asked". */}
          None recorded — the hotel is loading under the customer&rsquo;s own
          rate access code.
        </p>
      ) : (
        <ul className="detail-list" data-testid={`${testId}-list`}>
          {racs.map((r) => (
            <li data-testid={`${testId}-item-${r.id}`} key={r.id}>
              <span className="mono">{r.rac}</span>{' '}
              <span className="muted">· {r.gds}</span>
              {r.note ? <span className="muted"> · {r.note}</span> : null}
              {canEdit && (
                <button
                  className="btn btn-sm"
                  data-testid={`${testId}-remove-${r.id}`}
                  disabled={busy !== null}
                  onClick={() => void remove(r)}
                  style={{ marginLeft: 8 }}
                  type="button"
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {adding && (
        <RacForm
          agreementId={agreementId}
          onClose={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            onChanged();
          }}
        />
      )}
      {canEdit && (
        <button
          className="btn btn-sm btn-outline"
          data-testid={`${testId}-add`}
          onClick={() => setAdding(true)}
          style={{ marginTop: 8 }}
          type="button"
        >
          Add a code
        </button>
      )}
    </>
  );
}

function RacForm({
  agreementId,
  onClose,
  onSaved,
  testId = 'agreement-rac-form',
}: {
  agreementId: string;
  onClose: () => void;
  onSaved: () => void;
  testId?: string;
}) {
  const [rac, setRac] = useState('');
  const [gds, setGds] = useState<string>(GDS_OPTIONS[0]);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (saving || !rac.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await apiFetch('/api/btp/agreement-racs', {
        method: 'POST',
        body: JSON.stringify({
          agreement_id: agreementId,
          // NOT upper-cased. The code is quoted back to a hotel verbatim, and
          // the export carries mixed-case values that are real.
          rac: rac.trim(),
          gds,
          note,
        }),
      });
      onSaved();
    } catch (err) {
      // Includes the 409 the unique key produces, whose message names the code
      // and the GDS — actionable where "internal error" would not be.
      setError(messageOf(err, 'The code could not be saved.'));
      setSaving(false);
    }
  }

  return (
    <Modal
      actions={
        <>
          <button
            className="btn btn-outline"
            data-testid={`${testId}-cancel`}
            disabled={saving}
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            data-testid={`${testId}-save`}
            disabled={saving || !rac.trim()}
            onClick={() => void save()}
            type="button"
          >
            {saving ? 'Saving…' : 'Add code'}
          </button>
        </>
      }
      onClose={onClose}
      testId={testId}
      title="Alternate rate access code"
    >
      <form
        data-testid={`${testId}-form`}
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <p className="page-intro" style={{ marginTop: 0 }}>
          The code the hotel loaded <strong>this agreement</strong> under, when
          it differs from the customer&rsquo;s own. One per GDS.
        </p>

        {error && (
          <div
            className="banner banner-error"
            data-testid={`${testId}-error`}
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="form-grid">
          <div>
            <label className="field-label" htmlFor={`${testId}-rac`}>
              Code
            </label>
            <input
              className="input mono"
              data-testid={`${testId}-rac`}
              id={`${testId}-rac`}
              onChange={(e) => setRac(e.target.value)}
              placeholder="0FR"
              value={rac}
            />
          </div>
          <div>
            <label className="field-label" htmlFor={`${testId}-gds`}>
              GDS
            </label>
            <select
              className="select"
              data-testid={`${testId}-gds`}
              id={`${testId}-gds`}
              onChange={(e) => setGds(e.target.value)}
              value={gds}
            >
              {GDS_OPTIONS.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label className="field-label" htmlFor={`${testId}-note`}>
              Note
            </label>
            <input
              className="input"
              data-testid={`${testId}-note`}
              id={`${testId}-note`}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Why this code, or who is handling it"
              value={note}
            />
          </div>
        </div>
      </form>
    </Modal>
  );
}

function messageOf(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string } | null;
    return body?.error ?? err.message;
  }
  return err instanceof Error ? err.message : fallback;
}
